import type { IContentfulError } from "@services/sdk";
import ErrorResponse from "@services/sdk/models/error-response";
import {
	type ErrorResponseMap,
	ErrorResponseMapDefs,
} from "@services/sdk/models/error-response-map";
import type { AxiosError } from "axios";

enum ErrorResponseType {
	MRTM = 0,
	PSMA = 1,
	CONTENTFUL = 2,
	NONE = 3,
}

// biome-ignore lint/complexity/noStaticOnlyClass: <explanation>
class ErrorResponseFactory {
	public static Make =
		(errorResponseType: ErrorResponseType) =>
		(errorResponseMap?: ErrorResponseMap) =>
		(error: AxiosError | IContentfulError): ErrorResponse => {
			switch (errorResponseType) {
				case ErrorResponseType.MRTM:
					return ErrorResponse.ConstructMRTMErrorResponse(
						<ErrorResponseMap>errorResponseMap,
						<AxiosError>error,
					);
				case ErrorResponseType.CONTENTFUL:
					return ErrorResponse.ConstructContentfulErrorResponse(
						<IContentfulError>error,
					);
				case ErrorResponseType.PSMA:
					return ErrorResponse.ConstructPSMAErrorResponse(
						<ErrorResponseMap>errorResponseMap,
						<AxiosError>error,
					);
				default:
					return ErrorResponse.ConstructFromAxiosError(
						<ErrorResponseMap>errorResponseMap,
						<AxiosError>error,
					);
			}
		};

	public static PSMA = ErrorResponseFactory.Make(ErrorResponseType.PSMA);

	public static MRTM = ErrorResponseFactory.Make(ErrorResponseType.MRTM);

	public static Contentful = ErrorResponseFactory.Make(
		ErrorResponseType.CONTENTFUL,
	);

	public static ErrorDefaults = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.Defaults,
	);

	public static ErrorGetProducts = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.GetProducts,
	);

	public static ErrorGetPlan = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.GetPlan,
	);

	public static ErrorGetPlanSummary = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.GetPlanSummary,
	);

	public static ErrorVerifyPhoneNumber = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.VerifyPhoneNumber,
	);

	public static ErrorValidatePassword = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.ValidatePassword,
	);

	public static ErrorChangePassword = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.ChangePassword,
	);

	public static ErrorVerifyEmail = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.VerifyEmail,
	);

	public static ErrorVerifyPromoCode = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.VerifyPromoCode,
	);

	public static ErrorOnboarding = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.Onboarding,
	);

	public static ErrorGetDeliveryETA = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.GetDeliveryETA,
	);

	public static ErrorGetAddress = ErrorResponseFactory.PSMA(
		ErrorResponseMapDefs.GetAddress,
	);

	public static ErrorGetAddressSuggestions = ErrorResponseFactory.PSMA(
		ErrorResponseMapDefs.GetAddressSuggestions,
	);

	public static ErrorGetPaymentToken = ErrorResponseFactory.MRTM({}); // no specific error responses for this endpoint

	public static ErrorGetWelcomeCarouselSlides =
		ErrorResponseFactory.Contentful();

	public static ErrorGetLegalContent = ErrorResponseFactory.Contentful();

	public static ErrorGetImpactPage = ErrorResponseFactory.Contentful();

	public static ErrorGetImpactRanges = ErrorResponseFactory.Contentful();

	public static ErrorGetFAQPage = ErrorResponseFactory.Contentful();

	public static ErrorGetAsset = ErrorResponseFactory.Contentful();

	public static ErrorActivateVerifyEmail = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.ActivateVerifyEmail,
	);

	public static ErrorVerifyOTPCode = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.VerifyOTPCode,
	);

	public static ErrorLogin = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.Login,
	);

	public static ErrorVerifySIM = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.VerifySIM,
	);

	public static ErrorActivateService = ErrorResponseFactory.MRTM(
		ErrorResponseMapDefs.ActivateService,
	);
}

export default ErrorResponseFactory;
